import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, Send } from 'lucide-react';
import { forgotPassword, selectAuthStatus, selectAuthError, clearError } from '../features/auth/authSlice';
import SEO from './SEO';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);

  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const validateEmail = (email) => {
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    dispatch(clearError());

    if (!email.trim()) {
      setValidationError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    try {
      await dispatch(forgotPassword({ email: email.trim().toLowerCase() })).unwrap();
      setEmailSent(true);
    } catch (err) {
      console.error('Forgot password failed:', err);
    }
  };

  const handleGoBack = () => {
    navigate('/login');
  };

  // Success view - email sent
  if (emailSent) {
    return (
      <>
        <SEO
          title="Check Your Email - myCleanStudio"
          description="Password reset instructions have been sent to your email"
          url="/forgot-password"
        />
        <div className="forgot-password-page">
          <div className="forgot-password-container">
            <div className="forgot-password-header">
              <div className="forgot-password-icon success-icon">
                <CheckCircle size={32} color="white" />
              </div>
              <h2 className="forgot-password-title">Check your email</h2>
              <p className="forgot-password-subtitle">
                If an account exists for <strong>{email}</strong>, we've sent password reset instructions.
              </p>
            </div>

            <div className="info-box">
              <p>The email should arrive within a few minutes. Be sure to check your spam folder if you don't see it.</p>
            </div>

            <div className="forgot-password-actions">
              <button
                type="button"
                onClick={() => setEmailSent(false)}
                className="resend-btn"
              >
                <Send size={16} />
                Try another email
              </button>

              <Link to="/login" className="back-to-login-btn">
                <ArrowLeft size={16} />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Request form view
  return (
    <>
      <SEO
        title="Forgot Password - myCleanStudio"
        description="Reset your myCleanStudio password"
        url="/forgot-password"
      />
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-header">
            <div className="forgot-password-icon">
              <Mail size={32} color="white" />
            </div>
            <h2 className="forgot-password-title">Forgot your password?</h2>
            <p className="forgot-password-subtitle">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {(error || validationError) && (
            <div className="error-message">
              {validationError || error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="forgot-password-form">
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={status === 'loading'}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <>
                  <span className="btn-spinner"></span>
                  Sending...
                </>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>

          <div className="forgot-password-footer">
            <button
              type="button"
              onClick={handleGoBack}
              className="go-back-btn"
            >
              <ArrowLeft size={16} />
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
